import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";
import { ClipboardModule } from "ngx-clipboard";
import { Subscription as RxSubscription } from "rxjs";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";

import { Location } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { ActivatedRoute, Router, RouterEvent, RouterLink, RouterLinkActive } from "@angular/router";

import { StatusToIconPipe } from "../../../helpers/statusiconpipe/statusiconpipe";
import { AuthService } from "../../../services/authservice";
import { NWD_VERSION } from "../../../version";
import { SelfserviceStatus } from "../../selfservice/selfservice-status/selfservice-status";
import { ServiceIcon } from "../../subscription-detail/service-icon/service-icon";
import { GroteStoringBanner } from "./components/grote-storing-banner/grote-storing-banner";
import { MaintenanceBannerComponent } from "./components/maintenance-banner/maintenance-banner";
import { NavigationTopbarComponent } from "./components/navigation-topbar/navigation-topbar";
import { UserInterviewBanner } from "./components/user-interview-banner/user-interview-banner";
import { constructPageTitle, constructWelcomeString } from "./helpers";

@Component({
  selector: "navigation",
  templateUrl: "navigation.html",
  styleUrls: ["navigation.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AngularSvgIconModule,
    ClipboardModule,
    GroteStoringBanner,
    MaintenanceBannerComponent,
    UserInterviewBanner,
    NavigationTopbarComponent,
    RouterLink,
    RouterLinkActive,
    SelfserviceStatus,
    ServiceIcon,
    StatusToIconPipe,
    TranslatePipe,
  ],
})
export class NavigationComponent implements AfterViewInit, OnInit, OnDestroy {
  protected auth = inject(AuthService);
  protected router = inject(Router);
  protected translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private location = inject(Location);

  public readonly backEnabled = input(true);

  protected readonly detailData = input<{ title: string; status: string }>();
  protected readonly firewallIconVisible = input(false);
  protected readonly forceTitle: Signal<string | null> = input(null);
  protected readonly isSticky = input(true);
  protected readonly productType = input("");
  protected readonly subNavItems = input<{ link: string; title: string }[]>([]);
  protected readonly terminated = input(false);

  protected readonly isChildRoute = input(false);

  protected localIsChildRoute = signal(this.isChildRoute());
  protected localProductType = computed(() => {
    this.pageURL(); // re-reads route snapshot when URL changes
    return this.route.snapshot.paramMap.get("productType") || this.productType();
  });

  protected isFirewall = computed(() => this.localProductType().toLowerCase() === "fw");
  protected isWireless = computed(() => this.localProductType() === "Wireless");

  protected iconType = computed(() => this.localProductType().toLowerCase());
  // Firewall subscriptions get their own red icon; the overlap badge marks other services behind a firewall.
  protected firewallOverlapVisible = computed(() => this.firewallIconVisible() && !this.isFirewall());

  private pageURL = signal<string>("");

  private isDomainsPage = computed(() => this.pageURL().startsWith("/domains"));
  private detailPage = computed(
    () => this.pageURL().startsWith("/diensten/") && this.pageURL().split("/").filter(Boolean).length > 2,
  );
  private ipPrefixesPage = computed(() => this.pageURL() === "/ip-prefixes");

  protected domainsIconVisible = computed(() => this.isDomainsPage());
  protected IPPrefixIconVisible = computed(() => this.ipPrefixesPage());
  protected certIconVisible = computed(() => this.pageURL().startsWith("/cert"));
  protected iotnetIconVisible = computed(() => this.pageURL().startsWith("/iotnet"));

  private isNewsPage = computed(() => this.pageURL().startsWith("/news"));

  protected isDetailPage = computed(() => this.detailPage());

  private hasHistory = signal(false);

  protected maintenanceBanner = computed(() => this.auth.configuration().maintenanceBanner);
  protected showMaintenanceBanner = computed(() => this.maintenanceBanner().enabled);

  protected mainNavText = signal<string>("");
  protected routeTitle = signal<string | undefined>(undefined);
  protected nwdReleaseVersion = NWD_VERSION;
  protected userCanStartSelfservice = computed(
    () => this.auth.currentUser() && this.auth.configuration() && this.auth.canRequestSelfServiceDialog(),
  );

  private routerSubscription;
  private roleEventSubscription: Subscription = Subscription.EMPTY;
  private pageTitle = signal<string>("");
  private rxSubscriptions: RxSubscription[] = [];

  constructor() {
    this.routerSubscription = this.subscribeToRouter(this.router);
    this.hasHistory.set(this.router.navigated);
  }

  ngOnInit() {
    this.setMainNavText();
    this.rxSubscriptions.push(
      this.auth.userLoaded.subscribe(() => {
        this.setMainNavText();
      }),
    );
  }

  ngAfterViewInit() {
    window.scrollTo(0, 0);
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
    this.rxSubscriptions.forEach((sub) => sub.unsubscribe());
    if (!this.roleEventSubscription.closed) {
      this.roleEventSubscription.unsubscribe();
    }
  }

  protected setRouteTitle(translationKey: string) {
    this.setRouteTitleOnly(translationKey);
    this.localIsChildRoute.set(false);
  }

  protected setRouteTitleOnly(translationKey: string) {
    this.translate.get(translationKey).subscribe((translation: string) => {
      this.routeTitle.set(translation);
    });
  }

  goBack() {
    window.scrollTo(0, 0);

    if (this.isDomainsPage()) {
      this.router.navigate(["/"]);
      return;
    }

    if (this.isNewsPage()) {
      this.location.back();
      return;
    }

    if (this.hasHistory()) {
      this.location.back();
    } else {
      this.router.navigate(["/"]);
    }
  }

  private subscribeToRouter(router) {
    return router.events
      .pipe(filter((e: RouterEvent): e is RouterEvent => e instanceof RouterEvent))
      .subscribe((e: RouterEvent) => {
        this.pageURL.set(e.url);
        this.pageTitle.set("");
        switch (this.pageURL()) {
          case "/":
          case "/dashboard":
            this.setRouteTitle("Navigation.Overview");
            break;
          case "/berichten":
            this.setRouteTitle("Navigation.Messages");
            break;
          case "/support":
            this.setRouteTitle("Navigation.Support");
            break;
          case "/storing-en-onderhoud":
            this.setRouteTitle("Global.Overview");
            this.pageTitle.set("Navigation.MalfunctionAndMaintenance");
            break;
          case "/diensten":
            this.setRouteTitle("Navigation.Subscriptions");
            break;
          case "/ip-prefixes":
            this.localIsChildRoute.set(true);
            this.pageTitle.set("Subscription.IP_PREFIX.Name");
            break;
          case "/notification-settings":
            this.localIsChildRoute.set(true);
            this.setRouteTitle("Global.Notifications");
            this.pageTitle.set("Navigation.NotificationSettings");
            break;
          case "/cert":
            this.localIsChildRoute.set(true);
            this.pageTitle.set("Subscription.Cert.Name");
            break;
          case "/cert/ddos":
            this.localIsChildRoute.set(true);
            this.pageTitle.set("Subscription.Cert.Name");
            break;
          default:
            this.localIsChildRoute.set(true);
            // @ts-expect-error: There is a null/undefined/typing issue; please fix me!
            this.routeTitle.set(null);

            if (this.pageURL().startsWith("/storing-en-onderhoud")) {
              this.setRouteTitle("Global.Overview");
              this.pageTitle.set("Navigation.MalfunctionAndMaintenance");
            }

            if (this.isDomainsPage()) {
              this.setRouteTitleOnly("Domains.Title");
              this.pageTitle.set("Domains.Title");
            }
        }
      });
  }

  private getMainNavText(): Promise<string> {
    if (this.localIsChildRoute()) {
      return constructPageTitle(this.translate, this.pageURL());
    } else if (this.pageTitle()) {
      return new Promise((resolve) => {
        this.translate.get(this.pageTitle()).subscribe(resolve);
      });
    }
    return constructWelcomeString(this.translate, this.auth.currentUser().displayName);
  }

  private setMainNavText() {
    // Fixme: ugly exception, fix logic
    if (this.pageURL() === "/ip-prefixes") {
      this.mainNavText.set("IP prefixes");
      return;
    }
    this.getMainNavText().then((value) => {
      this.mainNavText.set(value);
    });
  }
}
